import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface EmailPreferences {
  creatorDigest: boolean;
  patronDigest: boolean;
  productUpdates: boolean;
  marketing: boolean;
  communityUpdates: boolean;
  tips: boolean;
  surveys: boolean;
  promotions: boolean;
  unsubscribeAll: boolean;
}

const EmailPreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    creatorDigest: true,
    patronDigest: true,
    productUpdates: true,
    marketing: false,
    communityUpdates: true,
    tips: true,
    surveys: false,
    promotions: false,
    unsubscribeAll: false,
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/email-preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.preferences) {
          setPreferences(data.preferences);
        }
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const handleToggle = (key: keyof EmailPreferences) => {
    if (key === 'unsubscribeAll') {
      // Unsubscribe from all emails
      const allFalse = !preferences.unsubscribeAll;
      setPreferences({
        creatorDigest: !allFalse,
        patronDigest: !allFalse,
        productUpdates: !allFalse,
        marketing: false,
        communityUpdates: !allFalse,
        tips: !allFalse,
        surveys: false,
        promotions: false,
        unsubscribeAll: allFalse,
      });
    } else {
      setPreferences({
        ...preferences,
        [key]: !preferences[key],
        unsubscribeAll: false,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/email-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ preferences }),
      });

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Email Preferences</h1>
        <p className="mt-2 text-sm text-gray-600">
          Control what emails you receive from us
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Essential Emails */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Essential Emails</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Creator Digest</span>
                <p className="text-xs text-gray-500 mt-1">
                  Weekly summary of your creator activity
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('creatorDigest')}
                className={`${
                  preferences.creatorDigest ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.creatorDigest ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Patron Digest</span>
                <p className="text-xs text-gray-500 mt-1">
                  Weekly updates from creators you support
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('patronDigest')}
                className={`${
                  preferences.patronDigest ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.patronDigest ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Platform Updates */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Platform Updates</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Product Updates</span>
                <p className="text-xs text-gray-500 mt-1">
                  New features and improvements
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('productUpdates')}
                className={`${
                  preferences.productUpdates ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.productUpdates ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Community Updates</span>
                <p className="text-xs text-gray-500 mt-1">
                  News from the community
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('communityUpdates')}
                className={`${
                  preferences.communityUpdates ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.communityUpdates ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Tips & Best Practices</span>
                <p className="text-xs text-gray-500 mt-1">
                  Learn how to get the most out of the platform
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('tips')}
                className={`${
                  preferences.tips ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.tips ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Marketing Emails */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Marketing Emails</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Marketing & Promotions</span>
                <p className="text-xs text-gray-500 mt-1">
                  Special offers and campaigns
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('marketing')}
                className={`${
                  preferences.marketing ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.marketing ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">Surveys & Research</span>
                <p className="text-xs text-gray-500 mt-1">
                  Help us improve with your feedback
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('surveys')}
                className={`${
                  preferences.surveys ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.surveys ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Promotions for Creators
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Exclusive deals and opportunities
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleToggle('promotions')}
                className={`${
                  preferences.promotions ? 'bg-indigo-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
              >
                <span
                  className={`${
                    preferences.promotions ? 'translate-x-5' : 'translate-x-0'
                  } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Unsubscribe All */}
        <div className="bg-red-50 rounded-lg border border-red-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-red-900">
                Unsubscribe from all emails
              </span>
              <p className="text-xs text-red-700 mt-1">
                You'll still receive important account and transactional emails
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('unsubscribeAll')}
              className={`${
                preferences.unsubscribeAll ? 'bg-red-600' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
            >
              <span
                className={`${
                  preferences.unsubscribeAll ? 'translate-x-5' : 'translate-x-0'
                } inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>

        {success && (
          <div className="flex items-center justify-center p-4 bg-green-50 rounded-md">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-800">
              Email preferences saved successfully!
            </span>
          </div>
        )}
      </form>
    </div>
  );
};

export default EmailPreferencesPage;
